import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

import CountrySelect from "../../../components/common/CountrySelect";
import MultiSelect from "../../../components/common/MultiSelect";
import PhoneInputField from "../../../components/common/PhoneInputField";
import Select from "../../../components/common/Select";
import ProfilePhotoUpload from "../../../components/professional/ProfilePhotoUpload";
import { RequiredAsterisk } from "../../../components/professional/RequiredAsterisk";
import { convertStringToOptions } from "../../../utils/helpers";
import { Trash2 } from "lucide-react";

const SOCIAL_MEDIA_PLATFORMS = [
  {
    label: "LinkedIn",
    key: "linkedin",
    placeholder: "https://linkedin.com/in/username",
  },
  {
    label: "Twitter / X",
    key: "twitter",
    placeholder: "https://x.com/username",
  },
  // {
  //   label: "GitHub",
  //   key: "github",
  //   placeholder: "https://github.com/username",
  // },
  {
    label: "Facebook",
    key: "facebook",
    placeholder: "https://facebook.com/username",
  },
  {
    label: "Instagram",
    key: "instagram",
    placeholder: "https://instagram.com/username",
  },
  { label: "Website", key: "website", placeholder: "https://yourwebsite.com" },
];

const PersonalInfoForm = ({
  data,
  onSave,
  onCancel,
  isEditMode = false,
  errors,
  setErrors,
  dropDownOptions = {},
}) => {
  const languageList = (dropDownOptions.languageChoices || []).some((c) => c.value === "other")
    ? dropDownOptions.languageChoices
    : [...(dropDownOptions.languageChoices || []), { value: "other", label: "Other" }];

  const availabilityList = (dropDownOptions.availabilityChoices || []).some((c) => c.value === "other")
    ? dropDownOptions.availabilityChoices
    : [...(dropDownOptions.availabilityChoices || []), { value: "other", label: "Other" }];

  const initialLanguage = data.personal_info?.language || "";
  const isLanguageInOptions = languageList.some((c) => c.value === initialLanguage);
  const isOtherLanguage = initialLanguage && !isLanguageInOptions;

  const initialAvailability = data.personal_info?.availability || "";
  const isAvailabilityInOptions = availabilityList.some((c) => c.value === initialAvailability);
  const isOtherAvailability = initialAvailability && !isAvailabilityInOptions;

  const [formData, setFormData] = useState({
    // top-level
    full_name: data.full_name,
    first_name: data.first_name || "",
    last_name: data.last_name || "",
    email: data.email || "",
    profile_picture: data.profile_picture || null,
    social_media_links: data.social_media_links || {},
    // nested personal_info
    phone_number: data.personal_info?.phone_number || "",
    date_of_birth: data.personal_info?.date_of_birth || "",
    nationality: data.personal_info?.nationality || "",
    national_id: data.personal_info?.national_id || "",
    city: data.personal_info?.city || "",
    country: data.personal_info?.country || "",
    language: isOtherLanguage ? "other" : (initialLanguage || ""),
    other_language: isOtherLanguage ? initialLanguage : (data.personal_info?.other_language || ""),
    availability: isOtherAvailability ? "other" : (initialAvailability || ""),
    other_availability: isOtherAvailability ? initialAvailability : (data.personal_info?.other_availability || ""),
    willing_to_relocate: data.personal_info?.willing_to_relocate || false,
    preferred_work_regions: data.personal_info?.preferred_work_regions || "",
    professional_bio: data.personal_info?.professional_bio || "",
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleCheckboxChange = (field, checked) => {
    handleChange(field, checked);
  };

  // Fix the handler — prev.social_medial_links was undefined, so spread was a no-op
  const handleSocialLinkChange = (key, value) => {
    setFormData((prev) => {
      const current = prev.social_media_links || {};
      if (value.trim()) {
        return { ...prev, social_media_links: { ...current, [key]: value } };
      }
      const { [key]: _, ...rest } = current;
      return { ...prev, social_media_links: rest };
    });
  };

  const validateForm = () => {
    let valid = true;
    const personalInfo = formData;

    if (!personalInfo.full_name?.trim()) {
      setErrors((prev) => ({
        ...prev,
        full_name: "Name is required",
      }));
      valid = false;
    }

    if (!personalInfo.phone_number?.trim()) {
      setErrors((prev) => ({
        ...prev,
        phone_number: "Phone number is required",
      }));
      valid = false;
    } else if (!isValidPhoneNumber(personalInfo.phone_number)) {
      setErrors((prev) => ({
        ...prev,
        phone_number: "Please enter a valid phone number",
      }));
      valid = false;
    }

    if (!personalInfo.date_of_birth) {
      setErrors((prev) => ({
        ...prev,
        date_of_birth: "Date of birth is required",
      }));
      valid = false;
    }

    if (!personalInfo.nationality?.trim()) {
      setErrors((prev) => ({
        ...prev,
        nationality: "Nationality is required",
      }));
      valid = false;
    }

    if (!personalInfo.city?.trim()) {
      setErrors((prev) => ({ ...prev, city: "City is required" }));
      valid = false;
    }

    if (!personalInfo.country?.trim()) {
      setErrors((prev) => ({ ...prev, country: "Country is required" }));
      valid = false;
    }

    if (!personalInfo.language?.trim()) {
      setErrors((prev) => ({ ...prev, language: "Language is required" }));
      valid = false;
    } else if (personalInfo.language === "other" && !personalInfo.other_language?.trim()) {
      setErrors((prev) => ({ ...prev, other_language: "Please specify your language" }));
      valid = false;
    }

    if (!personalInfo.availability?.trim()) {
      setErrors((prev) => ({
        ...prev,
        availability: "Availability is required",
      }));
      valid = false;
    } else if (personalInfo.availability === "other" && !personalInfo.other_availability?.trim()) {
      setErrors((prev) => ({
        ...prev,
        other_availability: "Please specify availability",
      }));
      valid = false;
    }

    if (!personalInfo.profile_picture) {
      setErrors((prev) => ({
        ...prev,
        profile_picture: "Profile picture is required",
      }));
      valid = false;
    }
    return valid;
  };

  // Basic phone number validation
  const isValidPhoneNumber = (phoneNumber) => {
    // Remove any non-digit characters except + for international numbers
    const cleaned = phoneNumber.replace(/[^\d+]/g, "");
    // Basic validation - at least 8 digits
    return cleaned.replace("+", "").length >= 8;
  };

  const handlePhoneChange = (value) => {
    handleChange("phone_number", value);
  };

  const getFirstAndLastName = (fullname) => {
    const nameArray = fullname.split(" ");
    const [firstname, ...rest] = nameArray;
    const lastname = rest.join(" ");
    return [firstname, lastname];
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const payload = {
        full_name: formData.full_name,
        email: formData.email,
        profile_picture: formData.profile_picture,
        social_media_links: formData.social_media_links,
        personal_info: {
          phone_number: formData.phone_number,
          date_of_birth: formData.date_of_birth,
          nationality: formData.nationality,
          national_id: formData.national_id,
          city: formData.city,
          country: formData.country,
          language: formData.language === "other" ? (formData.other_language?.trim() || "other") : formData.language,
          availability: formData.availability,
          other_availability: formData.availability === "other" ? formData.other_availability?.trim() : undefined,
          willing_to_relocate: formData.willing_to_relocate,
          preferred_work_regions: formData.preferred_work_regions,
          professional_bio: formData.professional_bio,
        },
      };
      onSave(payload);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, mt: 3, borderRadius: 3 }}>
      <form onSubmit={handleSubmit} className="sentry-mask">
        <Box
          sx={{
            px: 2,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <ProfilePhotoUpload
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            setErrors={setErrors}
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="full_name"
                fullWidth
                label={
                  <span>
                    Full Name <RequiredAsterisk />
                  </span>
                }
                // variant="outlined"
                value={formData.full_name}
                onChange={(e) => handleChange("full_name", e.target.value)}
                error={!!errors.full_name}
                helperText={errors.full_name}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="email"
                label={
                  <span>
                    Email <RequiredAsterisk />
                  </span>
                }
                fullWidth
                variant="outlined"
                value={formData.email}
                disabled
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <PhoneInputField
                errors={errors}
                onChange={handlePhoneChange}
                value={formData.phone_number}
              />
              {errors.phone_number && (
                <FormHelperText error sx={{ mt: 1 }}>
                  {errors.phone_number}
                </FormHelperText>
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="date_of_birth"
                fullWidth
                variant="outlined"
                label={
                  <span>
                    Date of Birth <RequiredAsterisk />
                  </span>
                }
                type="date"
                value={formData.date_of_birth}
                onChange={(e) => handleChange("date_of_birth", e.target.value)}
                error={!!errors.date_of_birth}
                helperText={errors.date_of_birth}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="nationality"
                fullWidth
                variant="outlined"
                value={formData.nationality}
                onChange={(e) => handleChange("nationality", e.target.value)}
                error={!!errors.nationality}
                helperText={errors.nationality}
                label={
                  <span>
                    Nationality <RequiredAsterisk />
                  </span>
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="national_id"
                fullWidth
                variant="outlined"
                value={formData.national_id}
                onChange={(e) => {
                  const numOnly = e.target.value.replace(/\D/g, "");
                  handleChange("national_id", numOnly);
                }}
                slotProps={{
                  htmlInput: {
                    inputMode: "numeric",
                    pattern: "[0-9]*",
                  },
                }}
                error={!!errors.national_id}
                helperText={errors.national_id}
                label="National ID"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="city"
                fullWidth
                variant="outlined"
                value={formData.city}
                onChange={(e) => handleChange("city", e.target.value)}
                error={!!errors.city}
                helperText={errors.city}
                label={
                  <span>
                    City <RequiredAsterisk />
                  </span>
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <CountrySelect
                value={formData.country}
                id="country"
                onChange={(value) => handleChange("country", value)}
                error={!!errors.country}
                helperText={errors.country}
                required
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                error={!!errors.language}
                helperText={errors.language}
                label={
                  <span>
                    Language <RequiredAsterisk />
                  </span>
                }
                options={languageList}
                onChange={(val) => handleChange("language", val)}
                value={formData.language}
              />
            </Grid>

            {formData.language === "other" && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="other_language"
                  fullWidth
                  variant="outlined"
                  value={formData.other_language || ""}
                  onChange={(e) => handleChange("other_language", e.target.value)}
                  error={!!errors.other_language}
                  helperText={errors.other_language}
                  label={
                    <span>
                      Specify Language <RequiredAsterisk />
                    </span>
                  }
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                error={!!errors.availability}
                helperText={errors.availability}
                label={
                  <span>
                    Availability <RequiredAsterisk />
                  </span>
                }
                options={availabilityList}
                onChange={(val) => handleChange("availability", val)}
                value={formData.availability}
              />
            </Grid>

            {formData.availability === "other" && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  id="other_availability"
                  fullWidth
                  variant="outlined"
                  value={formData.other_availability || ""}
                  onChange={(e) => handleChange("other_availability", e.target.value)}
                  error={!!errors.other_availability}
                  helperText={errors.other_availability}
                  label={
                    <span>
                      Specify Availability <RequiredAsterisk />
                    </span>
                  }
                />
              </Grid>
            )}

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.willing_to_relocate || false}
                    onChange={(e) =>
                      handleCheckboxChange(
                        "willing_to_relocate",
                        e.target.checked,
                      )
                    }
                  />
                }
                label="Willing to Relocate"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <MultiSelect
                id="preferred_work_regions"
                label={
                  <span>
                    Preferred work regions <RequiredAsterisk />
                  </span>
                }
                options={dropDownOptions.regionChoices}
                value={convertStringToOptions(
                  formData.preferred_work_regions,
                  dropDownOptions.regionChoices,
                )}
                onChange={(value) =>
                  handleChange("preferred_work_regions", value)
                }
                limitTags={3}
                error={!!errors.preferred_work_regions}
                helperText={errors.preferred_work_regions}
              />
              {/* <Select
                error={!!errors.preferred_work_regions}
                helperText={errors.preferred_work_regions}
                label={
                  <span>
                    Preferred work regions <RequiredAsterisk />
                  </span>
                }
                options={regionOptions}
                onChange={(val) => handleChange("preferred_work_regions", val)}
                value={formData.preferred_work_regions}
              /> */}
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Social Media Links
              </Typography>
              <Grid container spacing={2}>
                {SOCIAL_MEDIA_PLATFORMS.map(({ label, key, placeholder }) => (
                  <Grid key={key} size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={label}
                      placeholder={placeholder}
                      value={formData.social_media_links?.[key] || ""}
                      onChange={(e) =>
                        handleSocialLinkChange(key, e.target.value)
                      }
                      slotProps={{
                        input: {
                          // Clears the field cleanly
                          endAdornment: formData.social_media_links?.[key] ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={() => handleSocialLinkChange(key, "")}
                                edge="end"
                                aria-label={`Clear ${label}`}
                              >
                                <Trash2 size={16} />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                        },
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                value={formData.professional_bio || ""}
                onChange={(e) =>
                  handleChange("professional_bio", e.target.value)
                }
                label="Professional Bio"
                placeholder="Tell us about your aviation background and career goals..."
              />
              <FormHelperText>
                This will be visible to potential employers
              </FormHelperText>
            </Grid>
          </Grid>
        </Box>
        {isEditMode && (
          <div className="flex justify-end space-x-2 mt-4">
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              Save
            </Button>
          </div>
        )}
      </form>
    </Paper>
  );
};

export default PersonalInfoForm;
